import {cast, flow, Instance, types} from 'mobx-state-tree';
import {EStructureItem, hasValue} from '@progress-fe/core';
import {IEntityErrorMessage} from '@progress-fe/ui-kit';
import {v4 as uuidv4} from 'uuid';
import {SSE} from 'sse.js';
import {UserManager} from 'core/services/oidc.service';
import {DeveloperMode, RequestModel, ResetModel} from 'core/models';
import {checkpointDbStorage, projectDbStorage} from 'storages';
import {CheckpointsGetJournalOrderByEnum, VersionItem} from 'api/generated';
import {ECalculationStatus, EDumpReasonType, EJournalItemType} from 'core/enums';
import {PROJECT_LIST} from 'core/mocks/projects/projects.mocks';
import {ICheckpointUpdateEvent, IJournalPoint, ILastRunStatusEvent} from 'core/interfaces';
import {
  OilApi,
  ProjectOut,
  ProjectsApi,
  CheckpointOut,
  CheckpointsApi,
  TechProcessApi,
  JournalTaskRunPoint,
  CallbackRunResponse,
  JournalTaskRunPointEnum,
  ResponseStatusCheckpointOut
} from 'api';

import {Journal, UiState} from './models';

export const RUN_DELAY_MS = 1000;

const UI_PANELS_STORAGE_PREFIX = 'react-resizable-panels:';

const ProjectBase = types
  .compose(
    ResetModel,
    types.model('ProjectBase', {
      isRunning: false,
      isLoading: false,
      isConnected: false,

      checkpointUuid: '',
      checkpointLastSaving: types.maybeNull(types.Date),

      journal: types.optional(Journal, {}),
      uiState: types.optional(UiState, {}),
      developerMode: types.optional(DeveloperMode, {}),
      projectInfo: types.maybeNull(types.frozen<ProjectOut>()),
      versions: types.optional(types.array(types.frozen<VersionItem>()), []),

      runStatus: types.maybeNull(types.enumeration(Object.values(ECalculationStatus))),
      errors: types.optional(types.array(types.frozen<IEntityErrorMessage>()), []),

      versionsRequest: types.optional(RequestModel, {}),
      checkpointCreateRequest: types.optional(RequestModel, {}),
      checkpointChangeRequest: types.optional(RequestModel, {}),
      checkpointRestoreRequest: types.optional(RequestModel, {}),
      checkpointDeleteRequest: types.optional(RequestModel, {}),
      fetchCheckpointRequest: types.optional(RequestModel, {}),
      fetchRequest: types.optional(RequestModel, {}),
      journalRequest: types.optional(RequestModel, {}),
      runCallbackRequest: types.optional(RequestModel, {}),
      runRequest: types.optional(RequestModel, {})
    })
  )
  .volatile<{
    _sse: SSE | null;
  }>(() => ({
    _sse: null
  }))
  .volatile<{_runWaiter: NodeJS.Timeout | null; _runAttempt: number}>(() => ({
    _runWaiter: null,
    _runAttempt: 0
  }))
  .actions((self) => ({
    _restoreUiState: flow(function* () {
      const lastItem = yield projectDbStorage.getLastItem(self.checkpointUuid);
      if (hasValue(lastItem?.entityOrSubEntityId)) {
        const id = lastItem.entityOrSubEntityId;
        const lastItemState = yield checkpointDbStorage.getItemState(self.checkpointUuid, id);
        if (lastItemState) {
          self.uiState = UiState.create({
            tabIndex: lastItemState.tabIndex,
            entityId: lastItemState.entityId,
            entityType: lastItemState.entityType,
            subEntityId: lastItemState.subEntityId
          });
        } else if (id) {
          self.uiState = UiState.create({
            tabIndex: 0,
            entityId: id,
            entityType: lastItem.entityType,
            subEntityId: null
          });
        }
      } else if (hasValue(lastItem)) {
        self.uiState = UiState.create({entityType: lastItem.entityType});
      }
    })
  }))
  .actions((self) => ({
    _saveLastCheckpoint(projectId: string, checkpointId: string): void {
      projectDbStorage.addOrUpdateLastCheckpoint({projectId, checkpointId}).then();
    },
    _selectUiState(type: EStructureItem, uuid?: string | null, subUuid?: string | null): void {
      self.uiState.select(type, uuid, subUuid);

      // Restore tab index
      if (hasValue(self.uiState.dbItemStateId)) {
        checkpointDbStorage
          .getItemState(self.checkpointUuid, self.uiState.dbItemStateId)
          .then((state) => self.uiState._setTabIndex(state?.tabIndex ?? 0));
      }

      // Save last selected item
      projectDbStorage
        .addOrUpdateLastItem({
          entityType: type,
          checkpointId: self.checkpointUuid,
          entityOrSubEntityId: self.uiState.dbItemStateId
        })
        .then();
    },
    selectTabIndex(index: number) {
      checkpointDbStorage
        .addOrUpdateItemState(self.checkpointUuid, {
          id: self.uiState.dbItemStateId ?? '',
          entityId: self.uiState.entityId,
          entityType: self.uiState.entityType,
          subEntityId: self.uiState.subEntityId,
          tabIndex: index
        })
        .then();

      self.uiState._setTabIndex(index);
    }
  }))
  .actions((self) => ({
    _load: flow(function* (projectUuid: string) {
      const mockProject = PROJECT_LIST.find((p) => p.uuid === projectUuid);
      if (mockProject) {
        return mockProject;
      }

      const response: ProjectOut = yield self.fetchRequest.send(
        ProjectsApi.projectsGetProject.bind(ProjectsApi),
        {
          projectUuid
        }
      );

      return response ?? null;
    }),
    _loadVersions: flow(function* (projectUuid: string) {
      const response: VersionItem[] = yield self.versionsRequest.send(
        ProjectsApi.projectsGetProjectVersions.bind(ProjectsApi),
        {
          projectUuid
        }
      );

      self.versions = cast(response);
    }),
    _loadJournal: flow(function* () {
      const response: JournalTaskRunPoint[] = yield self.journalRequest.send(
        CheckpointsApi.checkpointsGetJournal.bind(CheckpointsApi),
        {
          projectUuid: self.projectInfo?.uuid ?? '',
          checkpointUuid: self.checkpointUuid,
          orderByField: JournalTaskRunPointEnum.RunAt,
          orderBy: CheckpointsGetJournalOrderByEnum.Desc
        }
      );

      if (response?.length) {
        const journalPoints: IJournalPoint[] = response.map((point) => ({
          runAt: point.runAt,
          isWarning: point.isWarning,
          status: point.status as ECalculationStatus,
          calculationNumber: point.calculationNumber,
          items: point.items.map((item) => ({
            type: item.type as EJournalItemType,
            createdAt: item.createdAt,
            message: item.message
          }))
        }));

        self.journal.clearPoints();
        self.journal.setPoints(journalPoints);
      } else {
        self.journal.clearPoints();
      }
    })
  }))
  .actions((self) => ({
    runCallback: flow(function* (callbackCode: string) {
      /* Run element callback */
      if (EStructureItem.Element === self.uiState.entityType) {
        const response: CallbackRunResponse = yield self.runCallbackRequest.send(
          TechProcessApi.techProcessRunElementCallback.bind(TechProcessApi),
          {
            projectUuid: self.projectInfo?.uuid ?? '',
            checkpointUuid: self.checkpointUuid,
            elementUuid: self.uiState.entityId,
            callbackRunRequest: {
              callbackCode
            }
          }
        );
        return response?.fieldValue ?? null;
      }
      /* Run logical element callback */
      if (EStructureItem.Task === self.uiState.entityType && !!self.uiState.entityId) {
        const response: CallbackRunResponse = yield self.runCallbackRequest.send(
          TechProcessApi.techProcessRunLogicalElementCallback.bind(TechProcessApi),
          {
            projectUuid: self.projectInfo?.uuid ?? '',
            checkpointUuid: self.checkpointUuid,
            logicalElementUuid: self.uiState.entityId,
            callbackRunRequest: {
              callbackCode
            }
          }
        );
        return response?.fieldValue ?? null;
      }
      /* Run model callback */
      if (EStructureItem.Model === self.uiState.entityType) {
        const response: CallbackRunResponse = yield self.runCallbackRequest.send(
          TechProcessApi.techProcessRunModelCallback.bind(TechProcessApi),
          {
            projectUuid: self.projectInfo?.uuid ?? '',
            checkpointUuid: self.checkpointUuid,
            modelInstanceUuid: self.uiState.entityId,
            callbackRunRequest: {
              callbackCode
            }
          }
        );
        return response?.fieldValue ?? null;
      }

      /* Run sample callback */
      if (EStructureItem.Sample === self.uiState.entityType) {
        const response: CallbackRunResponse = yield self.runCallbackRequest.send(
          OilApi.oilRunSampleCallback.bind(OilApi),
          {
            projectUuid: self.projectInfo?.uuid ?? '',
            checkpointUuid: self.checkpointUuid,
            sampleUuid: self.uiState.entityId,
            callbackRunRequest: {
              callbackCode
            }
          }
        );
        return response?.fieldValue ?? null;
      }
      /* Run blend callback */
      if (EStructureItem.Blend === self.uiState.entityType) {
        const response: CallbackRunResponse = yield self.runCallbackRequest.send(
          OilApi.oilRunBlendCallback.bind(OilApi),
          {
            projectUuid: self.projectInfo?.uuid ?? '',
            checkpointUuid: self.checkpointUuid,
            blendUuid: self.uiState.entityId,
            callbackRunRequest: {
              callbackCode
            }
          }
        );
        return response?.fieldValue ?? null;
      }
      return null;
    })
  }))
  .actions((self) => ({
    _clearErrors() {
      self.errors = cast([]);
    },
    _clearErrorByUuid(entityUuid: string, tabKey: string) {
      self.errors = cast(
        self.errors.filter((e) => !(e.entityUuid === entityUuid && e.tabKey === tabKey))
      );
    },
    _setRunResults(status: ILastRunStatusEvent | null): void {
      if (!status) {
        this._clearErrors();
        self.runStatus = ECalculationStatus.CalcResultError;
      } else {
        self.errors = cast(status.errorInfo);
        self.runStatus = status.status;
      }
    }
  }))
  .actions((self) => ({
    _setIsConnected(isConnected: boolean): void {
      self.isConnected = isConnected;
    },
    _setIsRunning(isRunning: boolean): void {
      self.isRunning = isRunning;
    },
    _setLastSave(date: Date): void {
      self.checkpointLastSaving = date;
    }
  }))
  .actions((self) => {
    const _sseSubscribe = flow(function* () {
      if (PROJECT_LIST.some((p) => p.uuid === self.projectInfo?.uuid)) {
        self._setIsConnected(true);
        return;
      }

      const user = yield UserManager.getUser();
      const params = `${self.projectUuid}/checkpoints/${self.checkpointUuid}`;
      const sse = new SSE(`/api/public/v1/projects/${params}/events`, {
        start: false,
        method: 'GET',
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
          'Content-Type': 'text/plain'
        }
      });

      sse.addEventListener('open', (e: {responseCode: number}) => {
        if (e.responseCode === 200) {
          console.info(`[SSE]: Connected.`);
          self._setIsConnected(true);
        }
      });

      sse.addEventListener('error', (e: unknown) => {
        console.error('[SSE] Error:', e);
        self._setIsConnected(false);
        console.info(`[SSE]: Reconnecting...`);
        _sseSubscribe().then();
      });

      /* CHECKPOINT EVENT */
      sse.addEventListener('checkpoint_update', (e: {data: string}) => {
        try {
          const event = JSON.parse(e.data) as ICheckpointUpdateEvent;
          self._setLastSave(new Date(event.last_saving_dt));
        } catch {
          console.warn('[SSE]: checkpoint_update event parse error: ', e);
        }
      });

      /* LAST RUN STATUS EVENT */
      sse.addEventListener('last_run_status', (e: {data: string}) => {
        try {
          const event = JSON.parse(e.data) as ILastRunStatusEvent;
          self._setIsRunning(event.status === ECalculationStatus.CalcInProgress);
          self._setRunResults(event);
        } catch {
          console.warn('[SSE]: last_run_status event parse error: ', e);
        }
      });

      self._sse = sse;
      sse.stream();
    });

    const _sseUnsubscribe = (): void => {
      self._sse?.close();
      self._sse = null;
    };

    return {_sseSubscribe, _sseUnsubscribe};
  })
  .actions((self) => ({
    refreshProject: flow(function* () {
      if (self.projectInfo?.uuid) {
        const project: ProjectOut | null = yield self._load(self.projectInfo?.uuid);
        if (project) {
          self.projectInfo = project;
        }
      }
    }),
    _baseInit: flow(function* (projectId: string, checkpointId: string) {
      const project: ProjectOut | null = yield self._load(projectId);
      const checkpoint = project?.checkpoints.find((c) => c.uuid === checkpointId);

      if (project && checkpoint) {
        self.projectInfo = project;
        self.checkpointUuid = checkpoint.uuid;
        self.checkpointLastSaving = checkpoint.lastSavingDt;

        yield self._restoreUiState();
        yield self._loadVersions(project.uuid);
        self._saveLastCheckpoint(project.uuid, checkpoint.uuid);
      }
    })
  }))
  .actions((self) => ({
    createCheckpoint: flow(function* () {
      const response: ResponseStatusCheckpointOut = yield self.checkpointCreateRequest.send(
        CheckpointsApi.checkpointsCreateCheckpoint.bind(CheckpointsApi),
        {
          projectUuid: self.projectInfo?.uuid ?? '',
          fromCheckpoint: self.checkpointUuid,
          idempotencyKey: uuidv4()
        }
      );

      return response?.data?.uuid ?? '';
    }),
    createCheckpointByCalcNumber: flow(function* (calcNumber: number, reason: EDumpReasonType) {
      const response: ResponseStatusCheckpointOut = yield self.checkpointRestoreRequest.send(
        CheckpointsApi.checkpointsRestoreCheckpointToNew.bind(CheckpointsApi),
        {
          projectUuid: self.projectInfo?.uuid ?? '',
          checkpointDumpKey: {
            saveReason: reason,
            calcNumber: calcNumber,
            checkpointUuid: self.checkpointUuid
          }
        }
      );

      return response?.data?.uuid;
    }),
    renameCheckpoint: flow(function* (uuid: string, name: string) {
      if (self.projectInfo) {
        self.projectInfo = {
          ...self.projectInfo,
          checkpoints: self.projectInfo.checkpoints.map((c) => ({
            ...c,
            name: c.uuid === uuid ? name : c.name
          }))
        };

        yield self.checkpointChangeRequest.send(
          CheckpointsApi.checkpointsUpdateCheckpoint.bind(CheckpointsApi),
          {
            projectUuid: self.projectInfo?.uuid ?? '',
            checkpointUuid: uuid,
            checkpointInUpdate: {
              name
            }
          }
        );
      }
    }),
    deleteCheckpoint: flow(function* (uuid: string) {
      yield self.checkpointDeleteRequest.send(
        CheckpointsApi.checkpointsDeleteCheckpoint.bind(CheckpointsApi),
        {
          projectUuid: self.projectInfo?.uuid ?? '',
          checkpointUuid: uuid
        }
      );

      if (self.checkpointDeleteRequest.isDone) {
        const checkpointPanelsKey = `${UI_PANELS_STORAGE_PREFIX}${uuid}`;
        localStorage.removeItem(checkpointPanelsKey);
      }

      if (self.checkpointDeleteRequest.isDone) {
        yield checkpointDbStorage._deleteDbs([uuid]);
        yield projectDbStorage.deleteLastItems([uuid]);
      }

      return self.checkpointDeleteRequest.isDone;
    })
  }))
  .views((self) => ({
    get isCheckpointCreating(): boolean {
      return self.checkpointCreateRequest.isPending;
    },
    get isCheckpointDeleting(): boolean {
      return self.checkpointDeleteRequest.isPending;
    }
  }))
  .views((self) => ({
    get projectUuid(): string {
      return self.projectInfo?.uuid ?? '';
    },
    get checkpoints(): Array<CheckpointOut> {
      return self.projectInfo?.checkpoints ?? [];
    },
    get isLoadError(): boolean {
      return self.fetchRequest.isError;
    }
  }));

export type TProjectBaseModel = Instance<typeof ProjectBase>;

export {ProjectBase};
